/**
 * tableDom.ts - Read-only access to the table's DOM for the interactions. Private to
 * `createTableInteractions`: nothing here is exposed on the model.
 */

import $ from 'jquery';
import type {TableVirtuosoHandle} from 'react-virtuoso';

import * as TableLayoutConstants from 'notebook/tables/constants/TableLayoutConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {computeIsBottomContainerSticky} from 'notebook/tables/store/logic/interactions/tableInteractionLogic';
import {computeCellCid} from 'notebook/tables/store/logic/TableIdentityLogic';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {TableViewsModel} from 'notebook/tables/store/selectors/tableModel/TableViewsSelectors';
import type {TableProps} from 'notebook/tables/store/TableStore';

/**
 * The table DOM the interactions read, kept current by the write-only `interactions.attach` callbacks.
 * Lives only inside the interactions' closure: it is never put on the model or in store state, so
 * attaching an element notifies no subscriber.
 */
export interface TableElements {
  /** The table's root element. */
  tableRoot: HTMLDivElement | null;
  focusBearer: HTMLDivElement | null;
  horizontalScrollContainer: HTMLDivElement | null;
  /** The page's vertical scroll container, found by `useTableMeasurement`. */
  pageVerticalScrollContainer: Element | null;
  stickyHeader: HTMLDivElement | null;
  virtualizationHandle: TableVirtuosoHandle | null;
}

export type TableVerticalScrollViews = Pick<TableViewsModel, 'isFixedHeight' | 'isMaximizedOrSplitScreen'>;

export interface TableDom {
  elements: Readonly<TableElements>;
  /**
   * The rendered cell at `index`. Cells left of the single-input boundary are found in the single-input
   * group, and every other cell in the table wrapper, so a single-input cell is never confused with its
   * copy elsewhere in the table.
   */
  findCellElement: (index: TableIndex) => Element | null;
  /**
   * The bounding box of the first element for the cell at `index` inside the horizontal scroll area.
   * Drags and scrolling measure cells this way, which differs from {@link findCellElement} for
   * single-input cells, so the two are kept separate.
   */
  getScrollAreaCellRect: (index: TableIndex) => DOMRect | undefined;
  /** The container that scrolls the table vertically for `views`: its own scroll area when height-limited. */
  getTableVerticalScrollContainer: (views: TableVerticalScrollViews) => HTMLElement | null;
  getSingleInputGroupHeight: () => number;
  /** The height of the footer (and persistent scrollbar) stuck to the bottom of the viewport, or 0. */
  getStickyFooterSize: () => number;
  showsPersistentHorizontalScrollbar: () => boolean;
}

export function createTableDom({
  elements,
  getTableModel,
  getTableProps,
}: {
  elements: Readonly<TableElements>;
  getTableModel: () => TableModel;
  getTableProps: () => TableProps;
}): TableDom {
  const showsPersistentHorizontalScrollbar = (): boolean => !getTableProps().viewer;

  return {
    elements,

    findCellElement: ([rowIndex, columnIndex]) => {
      const cellQuery = `tr td[data-cid='${computeCellCid(getTableProps().cid, rowIndex, columnIndex)}']`;
      const singleInputBoundaryIndex = getTableModel().singleInputs.boundaryIndex;
      return (
        elements.tableRoot?.querySelector(
          singleInputBoundaryIndex != null && columnIndex < singleInputBoundaryIndex
            ? `.mediocre-tableEditable-singleInputGroup-container ${cellQuery}`
            : `.mediocre-tableEditable-tableWrapper ${cellQuery}`
        ) ?? null
      );
    },

    getScrollAreaCellRect: ([i, j]) => {
      const scrollArea = elements.horizontalScrollContainer;
      if (!scrollArea) {
        return;
      }
      return $(scrollArea)
        .find(`[data-cid='${computeCellCid(getTableProps().cid, i, j)}']`)[0]
        ?.getBoundingClientRect();
    },

    getTableVerticalScrollContainer: (views) => {
      if (views.isFixedHeight || views.isMaximizedOrSplitScreen) {
        return elements.horizontalScrollContainer;
      }
      return (elements.pageVerticalScrollContainer as HTMLElement | null) ?? null;
    },

    getSingleInputGroupHeight: () =>
      elements.tableRoot?.querySelector('.mediocre-tableEditable-singleInputGroup')?.getBoundingClientRect()
        .height ?? 0,

    getStickyFooterSize: () => {
      const {views, visibility} = getTableModel();
      const isSticky = computeIsBottomContainerSticky({
        isFullHeight: views.isFullHeight,
        viewer: getTableProps().viewer ?? false,
        showFooter: visibility.showFooter,
      });
      if (!isSticky) {
        return 0;
      }
      return (
        TableLayoutConstants.TABLE_FOOTER_HEIGHT +
        (showsPersistentHorizontalScrollbar() ? TableLayoutConstants.NATIVE_SCROLLBAR_HEIGHT_ESTIMATE : 0)
      );
    },

    showsPersistentHorizontalScrollbar,
  };
}
